import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useCallback,
} from "react";
import {
  FormStep,
  ListingFormValues,
} from "@/components/listings/filters/FilterSchema";
import { UseFormReturn } from "react-hook-form";
import { Listing } from "@/lib/types";

interface ListingWizardContextType {
  form: UseFormReturn<ListingFormValues>;
  currentStep: FormStep;
  setCurrentStep: (step: FormStep) => void;
  listingId: number | undefined;
  setListingId: (id: number) => void;
  draftListing: Listing | undefined;
  setDraftListing: (listing: Listing) => void;
  isSubmitting: boolean;
  setIsSubmitting: (isSubmitting: boolean) => void;
  completedSteps: Record<number, boolean>;
  setStepCompleted: (step: FormStep, isCompleted: boolean) => void;
  canProceedToNextStep: boolean;
  setCanProceedToNextStep: (canProceed: boolean) => void;
  completionPercentage: number;
  formTouched: boolean;
  validateOnChange: boolean;
  setValidateOnChange: (validate: boolean) => void;
  saveCurrentStep: () => Promise<Listing | undefined>; // Updated return type
  publishListing: () => Promise<any>; // ADD THIS LINE
  saveFormDataToLocalStorage: (values: ListingFormValues) => void;
  loadFormDataFromLocalStorage: () => ListingFormValues | undefined;
}

const ListingWizardContext = createContext<
  ListingWizardContextType | undefined
>(undefined);

export function useListingWizardContext() {
  const context = useContext(ListingWizardContext);
  if (context === undefined) {
    throw new Error(
      "useListingWizardContext must be used within a ListingWizardProvider"
    );
  }
  return context;
}

interface ListingWizardProviderProps {
  children: ReactNode;
  value: ListingWizardContextType; // Accept the full context type
}

export function ListingWizardProvider({
  children,
  value,
}: ListingWizardProviderProps) {
  // The saveCurrentStep function is now expected to be part of the 'value' prop
  // from FormProvider.tsx, so we don't need the placeholder here.

  // The console.log('Saving current step...'); will now be removed as it was part of the placeholder.
  // The actual logging will happen inside the saveCurrentStep implementation in FormProvider.tsx.

  return (
    <ListingWizardContext.Provider value={value}>
      {" "}
      {/* Pass the full value directly */}
      {children}
    </ListingWizardContext.Provider>
  );
}
